import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { 
  Diamond, Flame, Trophy, Target, Calendar, CheckCircle2, 
  Plus, Edit, Trash2, Star, TrendingUp, Clock, Award,
  ChevronLeft, ChevronRight, Sparkles, Crown
} from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { API } from "./shared/utils";
import axios from "axios";

const QuestDashboard = ({ currentUser, challenges, members }) => {
  const [habits, setHabits] = useState([]);
  const [todayCompleted, setTodayCompleted] = useState([]);
  const [habitStreaks, setHabitStreaks] = useState({});
  const [streakData, setStreakData] = useState({ current_streak: 0, longest_streak: 0, total_days: 0, this_month_days: 0 });
  const [reflection, setReflection] = useState({ biggest_win: "", todays_learning: "", tomorrows_focus: "", notes: "" });
  const [reflectionSaved, setReflectionSaved] = useState(false);
  const [reflectionPoints, setReflectionPoints] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [questProgress, setQuestProgress] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationTitle, setCelebrationTitle] = useState("");
  const [diamondPoints, setDiamondPoints] = useState(currentUser?.diamond_points || 0);
  const [diamondStats, setDiamondStats] = useState(null);

  // Admin state
  const [showHabitAdmin, setShowHabitAdmin] = useState(false);
  const [allHabits, setAllHabits] = useState([]);
  const [newHabit, setNewHabit] = useState({ name: "", icon: "", daily_points: 10, display_order: 0 });
  const [editingHabit, setEditingHabit] = useState(null);
  const [showAwardDialog, setShowAwardDialog] = useState(false);
  const [awardData, setAwardData] = useState({ member_id: "", points: 10, reason: "" });
  const [diamondMembers, setDiamondMembers] = useState([]);

  const fetchData = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const [habitsRes, todayRes, streakRes, reflRes, lbRes, calRes, progressRes] = await Promise.all([
        axios.get(`${API}/diamond/habits`),
        axios.get(`${API}/diamond/habits/today/${currentUser.id}`),
        axios.get(`${API}/diamond/habits/streak/${currentUser.id}`),
        axios.get(`${API}/diamond/reflections/${currentUser.id}/today`),
        axios.get(`${API}/diamond/leaderboard`),
        axios.get(`${API}/diamond/habits/calendar/${currentUser.id}?month=${calendarMonth}`),
        axios.get(`${API}/diamond/quest-progress/${currentUser.id}`)
      ]);
      setHabits(habitsRes.data);
      setTodayCompleted(todayRes.data.completed || []);
      setHabitStreaks(todayRes.data.habit_streaks || {});
      setStreakData(streakRes.data);
      if (reflRes.data && reflRes.data.id) {
        setReflection(reflRes.data);
        setReflectionSaved(true);
      }
      setLeaderboard(lbRes.data);
      setCalendarData(calRes.data.days || {});
      setQuestProgress(progressRes.data || []);
      // Update diamond points from leaderboard data
      const myEntry = lbRes.data.find(e => e.id === currentUser.id);
      if (myEntry) setDiamondPoints(myEntry.diamond_points);
      // Fetch admin stats if admin
      if (currentUser.is_admin) {
        try {
          const statsRes = await axios.get(`${API}/diamond/stats`);
          setDiamondStats(statsRes.data);
        } catch (e) { /* non-critical */ }
      }
    } catch (err) {
      console.error("Error fetching quest data:", err);
    }
  }, [currentUser?.id, calendarMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Check for quest completion celebrations
  useEffect(() => {
    const completed = questProgress.filter(qp => qp.is_complete);
    if (completed.length > 0) {
      const shownKey = `quest_celebration_${completed[0].challenge_id}`;
      if (!sessionStorage.getItem(shownKey)) {
        sessionStorage.setItem(shownKey, 'true');
        setCelebrationTitle(completed[0].title);
        setShowCelebration(true);
      }
    }
  }, [questProgress]);

  const toggleHabit = async (habitId) => {
    try {
      const res = await axios.post(`${API}/diamond/habits/check`, {
        user_id: currentUser.id,
        habit_id: habitId
      });
      if (res.data.checked) {
        setTodayCompleted(prev => [...prev, habitId]);
        setDiamondPoints(prev => prev + (res.data.points_change || 0));
      } else {
        setTodayCompleted(prev => prev.filter(id => id !== habitId));
        setDiamondPoints(prev => prev + (res.data.points_change || 0));
      }
      // Refresh streaks (both overall and per-habit)
      const [streakRes, todayRes] = await Promise.all([
        axios.get(`${API}/diamond/habits/streak/${currentUser.id}`),
        axios.get(`${API}/diamond/habits/today/${currentUser.id}`)
      ]);
      setStreakData(streakRes.data);
      setHabitStreaks(todayRes.data.habit_streaks || {});
    } catch (err) {
      console.error("Error toggling habit:", err);
    }
  };

  const saveReflection = async () => {
    try {
      const res = await axios.post(`${API}/diamond/reflections`, {
        user_id: currentUser.id,
        ...reflection
      });
      setReflectionSaved(true);
      if (res.data.points_awarded > 0) {
        setReflectionPoints(res.data.points_awarded);
        setDiamondPoints(prev => prev + res.data.points_awarded);
        setTimeout(() => setReflectionPoints(0), 3000);
      }
    } catch (err) {
      console.error("Error saving reflection:", err);
    }
  };

  const handleAwardPoints = async () => {
    if (!awardData.member_id || awardData.points <= 0) return;
    try {
      await axios.post(`${API}/admin/diamond/award-points`, awardData);
      setShowAwardDialog(false);
      setAwardData({ member_id: "", points: 10, reason: "" });
      fetchData();
    } catch (err) {
      alert("Error awarding points: " + (err.response?.data?.detail || err.message));
    }
  };

  const fetchAllHabits = async () => {
    try {
      const res = await axios.get(`${API}/diamond/habits?active_only=false`);
      setAllHabits(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateHabit = async () => {
    if (!newHabit.name.trim()) return;
    try {
      await axios.post(`${API}/admin/diamond/habits`, newHabit);
      setNewHabit({ name: "", icon: "", daily_points: 10, display_order: 0 });
      fetchAllHabits();
      fetchData();
    } catch (err) { alert("Error creating habit"); }
  };

  const handleUpdateHabit = async () => {
    if (!editingHabit) return;
    try {
      await axios.put(`${API}/admin/diamond/habits/${editingHabit.id}`, editingHabit);
      setEditingHabit(null);
      fetchAllHabits();
      fetchData();
    } catch (err) { alert("Error updating habit"); }
  };

  const handleDeleteHabit = async (id) => {
    if (!window.confirm("Delete this habit?")) return;
    try {
      await axios.delete(`${API}/admin/diamond/habits/${id}`);
      fetchAllHabits();
      fetchData();
    } catch (err) { alert("Error deleting habit"); }
  };

  const changeMonth = (dir) => {
    const [y, m] = calendarMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    const nm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setCalendarMonth(nm);
  };

  // Calendar grid
  const renderCalendar = () => {
    const [year, month] = calendarMonth.split("-").map(Number);
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date().getDate();
    const isCurrentMonth = calendarMonth === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${calendarMonth}-${String(d).padStart(2, '0')}`;
      const completed = calendarData[dateStr] || [];
      const hasActivity = completed.length > 0;
      const isToday = isCurrentMonth && d === today;
      cells.push(
        <div key={d} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs relative
          ${isToday ? 'ring-2 ring-amber-400' : ''}
          ${hasActivity ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30' : 'bg-slate-800/30 border border-slate-700/30'}
        `}>
          <span className={hasActivity ? 'text-amber-300 font-bold' : 'text-slate-500'}>{d}</span>
          {hasActivity && <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-0.5" />}
        </div>
      );
    }
    return cells;
  };

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const [cYear, cMonth] = calendarMonth.split("-").map(Number);

  if (!currentUser) return null;

  return (
    <div className="space-y-6" data-testid="quest-dashboard">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#002957] via-[#662D91]/80 to-[#002957] p-6 md:p-8 border border-amber-500/20 shadow-2xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-50" />
        <div className="relative z-10 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 bg-amber-500/20 rounded-xl border border-amber-500/30">
                <Diamond className="w-7 h-7 text-amber-400" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">Diamond Quest</h2>
                <p className="text-amber-200/70 text-sm">Exclusive challenges designed to increase your Influence, Impact & Income</p>
              </div>
            </div>
            <p className="text-white/80 mt-3 text-sm">Welcome back, <span className="text-amber-300 font-semibold">{currentUser.name}</span></p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-400">{diamondPoints}</p>
              <p className="text-xs text-amber-200/60">Diamond Points</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-[#002957]/80 border-amber-500/20">
          <CardContent className="p-4 text-center">
            <Flame className="w-6 h-6 text-orange-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-orange-400">{streakData.current_streak}</p>
            <p className="text-xs text-slate-400">Current Streak</p>
          </CardContent>
        </Card>
        <Card className="bg-[#002957]/80 border-amber-500/20">
          <CardContent className="p-4 text-center">
            <Trophy className="w-6 h-6 text-amber-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-400">{streakData.longest_streak}</p>
            <p className="text-xs text-slate-400">Best Streak</p>
          </CardContent>
        </Card>
        <Card className="bg-[#002957]/80 border-amber-500/20">
          <CardContent className="p-4 text-center">
            <Calendar className="w-6 h-6 text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-purple-400">{streakData.this_month_days}</p>
            <p className="text-xs text-slate-400">This Month</p>
          </CardContent>
        </Card>
        <Card className="bg-[#002957]/80 border-amber-500/20">
          <CardContent className="p-4 text-center">
            <Star className="w-6 h-6 text-amber-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-400">{diamondPoints}</p>
            <p className="text-xs text-slate-400">Diamond Points</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Habits + Reflection */}
        <div className="lg:col-span-2 space-y-6">
          {/* Quest Progress */}
          {questProgress.length > 0 && (
            <Card className="bg-[#002957]/60 border-purple-500/20" data-testid="quest-progress-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2 text-base">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                    Quest Challenges
                  </CardTitle>
                  {currentUser.is_admin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDiamondMembers((members || []).filter(m => m.membership_type === 'diamond'));
                        setShowAwardDialog(true);
                      }}
                      className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-7 text-xs"
                      data-testid="award-dp-btn"
                    >
                      <Award className="w-3 h-3 mr-1" />
                      Award DP
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {questProgress.map(qp => (
                  <div key={qp.challenge_id} className={`p-3 rounded-lg border ${
                    qp.is_complete 
                      ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30'
                      : 'bg-gradient-to-r from-purple-500/10 to-amber-500/10 border-purple-500/20'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-white font-medium text-sm flex items-center gap-2">
                          {qp.title}
                          {qp.is_complete && <CheckCircle2 className="w-4 h-4 text-green-400" />}
                        </p>
                        <p className="text-slate-400 text-xs">{qp.description?.substring(0, 60)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-amber-400 font-bold text-sm">{qp.diamond_points_earned} DP</p>
                        <Badge className={`text-[10px] ${qp.status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-slate-600 text-slate-300'}`}>
                          {qp.status}
                        </Badge>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${qp.is_complete ? 'bg-green-500' : 'bg-gradient-to-r from-purple-500 to-amber-500'}`}
                          style={{ width: `${qp.completion_pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-400 min-w-[60px] text-right">
                        {qp.completed_tasks}/{qp.task_count} tasks
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* No quests yet + Admin award button */}
          {questProgress.length === 0 && currentUser.is_admin && (
            <Card className="bg-[#002957]/60 border-purple-500/20">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">No Diamond Exclusive challenges yet</p>
                  <p className="text-slate-500 text-xs">Create a challenge with "Diamond Exclusive" visibility</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setDiamondMembers((members || []).filter(m => m.membership_type === 'diamond'));
                    setShowAwardDialog(true);
                  }}
                  className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-7 text-xs"
                  data-testid="award-dp-btn-empty"
                >
                  <Award className="w-3 h-3 mr-1" />
                  Award DP
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Daily Habits */}
          <Card className="bg-[#002957]/60 border-amber-500/20" data-testid="daily-habits-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  Daily Habits
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/20 text-xs">
                    {todayCompleted.length}/{habits.length} done
                  </Badge>
                  {currentUser.is_admin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setShowHabitAdmin(true); fetchAllHabits(); }}
                      className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 h-7 text-xs"
                      data-testid="manage-habits-btn"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Manage
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {habits.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No habits configured yet</p>
                  {currentUser.is_admin && <p className="text-xs mt-1">Click "Manage" to add habits</p>}
                </div>
              ) : habits.map(habit => {
                const isChecked = todayCompleted.includes(habit.id);
                const streak = habitStreaks[habit.id] || 0;
                return (
                  <button
                    key={habit.id}
                    onClick={() => toggleHabit(habit.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
                      isChecked
                        ? 'bg-gradient-to-r from-green-500/15 to-emerald-500/15 border border-green-500/30'
                        : 'bg-slate-800/40 border border-slate-700/40 hover:border-slate-600/60'
                    }`}
                    data-testid={`habit-${habit.id}`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      isChecked ? 'bg-green-500 border-green-400' : 'border-slate-500'
                    }`}>
                      {isChecked && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                    <span className={`flex-1 font-medium text-sm ${isChecked ? 'text-green-300 line-through opacity-70' : 'text-white'}`}>
                      {habit.icon && <span className="mr-2">{habit.icon}</span>}
                      {habit.name}
                    </span>
                    {streak >= 3 && (
                      <span className="flex items-center gap-0.5 text-orange-400 text-xs font-bold" data-testid={`streak-${habit.id}`}>
                        <Flame className="w-3.5 h-3.5" />
                        {streak}d
                      </span>
                    )}
                    <Badge className={`text-xs ${isChecked ? 'bg-green-500/20 text-green-300' : 'bg-slate-700/50 text-slate-400'}`}>
                      +{habit.daily_points} DP
                    </Badge>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Daily Reflection */}
          <Card className="bg-[#002957]/60 border-indigo-500/20" data-testid="daily-reflection-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <TrendingUp className="w-5 h-5 text-indigo-400" />
                Today's Reflection
              </CardTitle>
              <CardDescription className="text-slate-400 text-xs">Capture your daily growth insights</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-amber-300">Biggest Win</Label>
                <Input
                  value={reflection.biggest_win || ""}
                  onChange={e => setReflection({...reflection, biggest_win: e.target.value})}
                  placeholder="What was your biggest win today?"
                  className="bg-slate-800/50 border-slate-700/50 text-white text-sm h-9 mt-1"
                  data-testid="reflection-win-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-blue-300">Today's Learning</Label>
                <Input
                  value={reflection.todays_learning || ""}
                  onChange={e => setReflection({...reflection, todays_learning: e.target.value})}
                  placeholder="What did you learn today?"
                  className="bg-slate-800/50 border-slate-700/50 text-white text-sm h-9 mt-1"
                  data-testid="reflection-learning-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-green-300">Tomorrow's Focus</Label>
                <Input
                  value={reflection.tomorrows_focus || ""}
                  onChange={e => setReflection({...reflection, tomorrows_focus: e.target.value})}
                  placeholder="What will you focus on tomorrow?"
                  className="bg-slate-800/50 border-slate-700/50 text-white text-sm h-9 mt-1"
                  data-testid="reflection-focus-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-400">Notes</Label>
                <Textarea
                  value={reflection.notes || ""}
                  onChange={e => setReflection({...reflection, notes: e.target.value})}
                  placeholder="Any other thoughts..."
                  className="bg-slate-800/50 border-slate-700/50 text-white text-sm mt-1"
                  rows={2}
                />
              </div>
              <Button
                onClick={saveReflection}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500"
                data-testid="save-reflection-btn"
              >
                {reflectionSaved ? "Update Reflection" : "Save Reflection (+5 DP)"}
              </Button>
              {reflectionSaved && (
                <p className="text-xs text-green-400 text-center">
                  Reflection saved for today
                  {reflectionPoints > 0 && <span className="ml-1 text-amber-400 font-bold animate-pulse">+{reflectionPoints} DP!</span>}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Calendar + Leaderboard */}
        <div className="space-y-6">
          {/* Calendar */}
          <Card className="bg-[#002957]/60 border-amber-500/20" data-testid="habit-calendar">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-400" />
                  Activity Calendar
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => changeMonth(-1)} className="h-6 w-6 p-0 text-slate-400 hover:text-white">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-slate-300 min-w-[100px] text-center">{monthNames[cMonth - 1]} {cYear}</span>
                  <Button size="sm" variant="ghost" onClick={() => changeMonth(1)} className="h-6 w-6 p-0 text-slate-400 hover:text-white">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {['S','M','T','W','T','F','S'].map((d,i) => (
                  <div key={i} className="text-center text-[10px] text-slate-500 font-medium">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {renderCalendar()}
              </div>
            </CardContent>
          </Card>

          {/* Diamond Leaderboard */}
          <Card className="bg-[#002957]/60 border-amber-500/20" data-testid="diamond-leaderboard">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-400" />
                Diamond Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {leaderboard.length === 0 ? (
                <p className="text-slate-500 text-xs text-center py-4">No Diamond members yet</p>
              ) : leaderboard.slice(0, 10).map((entry, idx) => (
                <div key={entry.id} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                  idx === 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-slate-800/30'
                }`}>
                  <span className={`w-5 text-center font-bold text-xs ${
                    idx === 0 ? 'text-amber-400' : idx === 1 ? 'text-slate-300' : idx === 2 ? 'text-orange-400' : 'text-slate-500'
                  }`}>{entry.rank}</span>
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-xs">
                    {entry.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-medium truncate">{entry.name}</p>
                    {entry.current_streak > 0 && (
                      <p className="text-orange-400 text-[10px]"><Flame className="w-3 h-3 inline" /> {entry.current_streak}d streak</p>
                    )}
                  </div>
                  <span className="text-amber-400 font-bold text-xs">{entry.diamond_points} DP</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Admin: Diamond Reports */}
      {currentUser.is_admin && diamondStats && (
        <Card className="bg-[#002957]/60 border-amber-500/20" data-testid="diamond-reports">
          <CardHeader className="pb-3">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              Diamond Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30 text-center">
                <p className="text-2xl font-bold text-amber-400">{diamondStats.total_diamond_members}</p>
                <p className="text-xs text-slate-400">Diamond Members</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30 text-center">
                <p className="text-2xl font-bold text-green-400">{diamondStats.active_today}</p>
                <p className="text-xs text-slate-400">Active Today</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30 text-center">
                <p className="text-2xl font-bold text-purple-400">{diamondStats.today_habit_completions}</p>
                <p className="text-xs text-slate-400">Habits Done Today</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30 text-center">
                <p className="text-2xl font-bold text-blue-400">{diamondStats.today_reflections}</p>
                <p className="text-xs text-slate-400">Reflections Today</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
                <p className="text-xs text-slate-400 mb-1">Most Completed Habit</p>
                <p className="text-white text-sm font-medium">{diamondStats.most_completed_habit || 'N/A'}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
                <p className="text-xs text-slate-400 mb-1">Least Completed Habit</p>
                <p className="text-white text-sm font-medium">{diamondStats.least_completed_habit || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin: Manage Habits Dialog */}
      <Dialog open={showHabitAdmin} onOpenChange={setShowHabitAdmin}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              Manage Diamond Habits
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add New Habit */}
            <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 space-y-2">
              <p className="text-xs font-medium text-amber-300">Add New Habit</p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={newHabit.name}
                  onChange={e => setNewHabit({...newHabit, name: e.target.value})}
                  placeholder="Habit name"
                  className="bg-slate-700 border-slate-600 text-white text-sm h-8"
                  data-testid="new-habit-name"
                />
                <Input
                  value={newHabit.icon}
                  onChange={e => setNewHabit({...newHabit, icon: e.target.value})}
                  placeholder="Icon (emoji)"
                  className="bg-slate-700 border-slate-600 text-white text-sm h-8"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-slate-400">Points/day</Label>
                  <Input
                    type="number"
                    value={newHabit.daily_points}
                    onChange={e => setNewHabit({...newHabit, daily_points: parseInt(e.target.value) || 0})}
                    className="bg-slate-700 border-slate-600 text-white text-sm h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Order</Label>
                  <Input
                    type="number"
                    value={newHabit.display_order}
                    onChange={e => setNewHabit({...newHabit, display_order: parseInt(e.target.value) || 0})}
                    className="bg-slate-700 border-slate-600 text-white text-sm h-8"
                  />
                </div>
              </div>
              <Button onClick={handleCreateHabit} size="sm" className="w-full bg-amber-600 hover:bg-amber-700 h-8" data-testid="create-habit-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Habit
              </Button>
            </div>

            {/* Existing Habits */}
            <div className="space-y-2">
              {allHabits.map(h => (
                <div key={h.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/30 border border-slate-700/30">
                  {editingHabit?.id === h.id ? (
                    <div className="flex-1 space-y-2">
                      <Input
                        value={editingHabit.name}
                        onChange={e => setEditingHabit({...editingHabit, name: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white text-sm h-8"
                      />
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          value={editingHabit.daily_points}
                          onChange={e => setEditingHabit({...editingHabit, daily_points: parseInt(e.target.value) || 0})}
                          className="bg-slate-700 border-slate-600 text-white text-sm h-8 w-20"
                        />
                        <Button size="sm" onClick={handleUpdateHabit} className="bg-green-600 h-8 text-xs">Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingHabit(null)} className="h-8 text-xs text-slate-400">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <p className="text-white text-sm">{h.icon} {h.name}</p>
                        <p className="text-xs text-slate-500">+{h.daily_points} DP | Order: {h.display_order} | {h.active ? 'Active' : 'Inactive'}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setEditingHabit({...h})} className="h-7 text-slate-400 hover:text-white">
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteHabit(h.id)} className="h-7 text-red-400 hover:text-red-300">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quest Completion Celebration */}
      <Dialog open={showCelebration} onOpenChange={setShowCelebration}>
        <DialogContent className="bg-gradient-to-br from-[#002957] to-[#662D91] border-amber-500/30 text-center max-w-sm">
          <div className="py-6 space-y-4">
            <div className="text-6xl animate-bounce">🎉</div>
            <h2 className="text-2xl font-bold text-white">Congratulations!</h2>
            <p className="text-amber-200 text-lg font-semibold">{celebrationTitle}</p>
            <p className="text-white/70 text-sm">Quest Completed</p>
            <div className="flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-amber-400 fill-amber-400" style={{animationDelay: `${i * 0.1}s`}} />
              ))}
            </div>
            <Button onClick={() => setShowCelebration(false)} className="bg-amber-500 hover:bg-amber-600 text-white mt-2">
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: Award Diamond Points Dialog */}
      <Dialog open={showAwardDialog} onOpenChange={setShowAwardDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-400" />
              Award Diamond Points
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-400">Diamond Member</Label>
              <select
                value={awardData.member_id}
                onChange={e => setAwardData({...awardData, member_id: e.target.value})}
                className="w-full h-9 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 mt-1"
                data-testid="award-member-select"
              >
                <option value="">Select member...</option>
                {diamondMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.diamond_points || 0} DP)</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Points to Award</Label>
              <Input
                type="number"
                min="1"
                value={awardData.points}
                onChange={e => setAwardData({...awardData, points: parseInt(e.target.value) || 0})}
                className="bg-slate-800 border-slate-700 text-white text-sm h-9 mt-1"
                data-testid="award-points-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Reason</Label>
              <Input
                value={awardData.reason}
                onChange={e => setAwardData({...awardData, reason: e.target.value})}
                placeholder="e.g. Bonus for consistency"
                className="bg-slate-800 border-slate-700 text-white text-sm h-9 mt-1"
                data-testid="award-reason-input"
              />
            </div>
            <Button
              onClick={handleAwardPoints}
              disabled={!awardData.member_id || awardData.points <= 0}
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              data-testid="confirm-award-btn"
            >
              <Award className="w-4 h-4 mr-2" />
              Award {awardData.points} DP
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestDashboard;
